import { useState } from 'react';
import { userService } from '../../../services/user.service';
import { useTranslation } from 'react-i18next';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import Alert from '../../UI/Alert/Alert';
import styles from './UserCreateFrom.module.css';
import { toast } from 'react-toastify';

function UserCreateForm({ isOpen, onClose, onSuccess }) {
    const { t } = useTranslation('auth');
    const { t: tCommon } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');

    const [email, setEmail] = useState({ value: '', error: null });
    const [password, setPassword] = useState({ value: '', error: null });
    const [confirmPassword, setConfirmPassword] = useState({ value: '', error: null });
    const [role, setRole] = useState({ value: '', error: null });
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const roles = [
        { value: 'admin', label: tCommon('users.roles.admin') },
        { value: 'inventor', label: tCommon('users.roles.inventor') },
        { value: 'expert', label: tCommon('users.roles.expert') }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setEmail({ value: email.value, error: null });
        setPassword({ value: password.value, error: null });
        setConfirmPassword({ value: confirmPassword.value, error: null });
        setRole({ value: role.value, error: null });
        setGeneralError(null);

        try {
            const response = await userService.createUser({
                email: email.value,
                password: password.value,
                confirmPassword: confirmPassword.value,
                code: role.value
            });

            if (response.status === 'success' || response.user) {
                // Сброс формы
                setEmail({ value: '', error: null });
                setPassword({ value: '', error: null });
                setConfirmPassword({ value: '', error: null });
                setRole({ value: '', error: null });
                setGeneralError(null);

                toast.success(tValidation('user_created_successfully'));
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach(error => {
                    if (error.path[0] === 'email') {
                        setEmail({ value: email.value, error: error });
                    }
                    if (error.path[0] === 'password') {
                        setPassword({ value: password.value, error: error });
                    }
                    if (error.path[0] === 'confirmPassword') {
                        setConfirmPassword({ value: confirmPassword.value, error: error });
                    }
                    if (error.path[0] === 'code') {
                        setRole({ value: role.value, error: error });
                    }
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity.toString());
                setGeneralError(message);
                toast.error(message);
            } else {
                const message = error.message || 'Something went wrong';
                setGeneralError(message);
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setEmail({ value: '', error: null });
            setPassword({ value: '', error: null });
            setConfirmPassword({ value: '', error: null });
            setRole({ value: '', error: null });
            setGeneralError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{t('createUser.title')}</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={isLoading}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {generalError && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}
                    <Input
                        type="email"
                        placeholder={t('createUser.email')}
                        value={email.value}
                        onChange={(e) => setEmail({ value: e.target.value, error: null })}
                        error={email.error}
                        required
                    />
                    <Input
                        type="password"
                        placeholder={t('createUser.password')}
                        value={password.value}
                        onChange={(e) => setPassword({ value: e.target.value, error: null })}
                        error={password.error}
                        required
                    />
                    <Input
                        type="password"
                        placeholder={t('createUser.confirmPassword')}
                        value={confirmPassword.value}
                        onChange={(e) => setConfirmPassword({ value: e.target.value, error: null })}
                        error={confirmPassword.error}
                        required
                    />
                    <Select
                        options={roles}
                        value={role.value}
                        onChange={(e) => setRole({ value: e.target.value, error: null })}
                        placeholder={t('createUser.role')}
                        error={role.error}
                        required
                    />
                    <div className={styles.buttons}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            {t('createUser.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {t('createUser.create')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default UserCreateForm;
